import { IncorrectCodeError } from '../Login/incorrectCodeError';
import { WrongTenantError } from '../Login/wrongTenantError';

const errorName = (error: unknown): string | undefined => {
  if (error == null || typeof error !== 'object') {
    return undefined;
  }
  const { name, code, __type } = error as { name?: unknown; code?: unknown; __type?: unknown };
  const value = __type ?? code ?? name;
  return typeof value === 'string' ? value : undefined;
};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : '';

/**
 * Whether the code on screen can still be submitted after this failure. Only a wrong answer that
 * Cognito re-challenged keeps the session alive (HRI2-18976); anything else - the last allowed
 * attempt, an expired session, a network failure - may have ended it, and re-submitting against an
 * ended session only gets a misleading "Invalid session for the user".
 */
export const otpCodeStillUsable = (error: unknown): boolean => error instanceof IncorrectCodeError;

/** The message to show after a failed OTP sign-in. */
export const otpSignInErrorMessage = (error: unknown): string => {
  if (error instanceof WrongTenantError || error instanceof IncorrectCodeError) {
    return error.message;
  }

  if (errorName(error) === 'NotAuthorizedException') {
    // Cognito reports a wrong answer and a dead session with the same name; only the message
    // tells them apart.
    if (/invalid session|session is expired/i.test(errorMessage(error))) {
      return 'This code has expired. Request a new code to sign in.';
    }
    // A rejected (rather than re-challenged) wrong answer means the auth flow was failed: the
    // attempts for this code have run out.
    return "Incorrect code, and this code can't be tried again. Request a new code to sign in.";
  }

  // A network failure or throttling may also have consumed the session, and there is no way to
  // tell from here, so ask for a new code rather than risk another silent dead end.
  return "We couldn't sign you in with that code. Request a new code and try again.";
};
